import isNil from 'lodash-es/isNil'

import { useIsMutating } from '@tanstack/react-query'

import { useOffer } from '../../../../hooks/useOffer'
import { createStockMutationKey, useCreateStockMutation } from '../mutations/useCreateStockMutation'
import { deleteStockMutationKey, useDeleteStockMutation } from '../mutations/useDeleteStockMutation'
import { updateStockMutationKey, useUpdateStockMutation } from '../mutations/useUpdateStockMutation'

type UseStockProps = {
  productId: string
  onSuccess?: (stock: { id: string; quantity: number | undefined }) => void
}

export const useStock = ({ productId, onSuccess }: UseStockProps) => {
  const { invalidateOffer } = useOffer(productId)
  const { createStockMutation } = useCreateStockMutation()
  const { updateStockMutation } = useUpdateStockMutation()
  const { deleteStockMutation } = useDeleteStockMutation()

  const isMutatingCreateStock = useIsMutating([createStockMutationKey])
  const isMutatingUpdateStock = useIsMutating([updateStockMutationKey])
  const isMutatingDeleteStock = useIsMutating([deleteStockMutationKey])

  const isUpdatingStock =
    isMutatingCreateStock > 0 || isMutatingUpdateStock > 0 || isMutatingDeleteStock > 0

  const handleStockCreate = (stock: { id: string; quantity: number | undefined }) => {
    if (stock.quantity === undefined) return

    createStockMutation.mutate([{ variantId: stock.id, quantity: stock.quantity }], {
      onSuccess: (data) => {
        invalidateOffer()
        onSuccess?.({ id: data.variantId, quantity: data.quantity })
      },
    })
  }

  const handleStockUpdate = (stock: { id: string; data: { from: number; to: number } }) => {
    updateStockMutation.mutate([{ variantId: stock.id }, stock.data], {
      onSuccess: (data) => {
        invalidateOffer()
        onSuccess?.({ id: data.variantId, quantity: data.quantity })
      },
    })
  }

  const handleStockDelete = (id: string) => {
    deleteStockMutation.mutate([{ variantId: id }], {
      onSuccess: () => {
        invalidateOffer()
        onSuccess?.({ id, quantity: undefined })
      },
    })
  }

  const handleStockChange = (id: string) => (oldValue?: number, newValue?: number) => {
    if (isNil(oldValue)) {
      return handleStockCreate({ id, quantity: newValue })
    }

    if (isNil(newValue)) {
      return handleStockDelete(id)
    }

    return handleStockUpdate({ id, data: { from: oldValue, to: newValue } })
  }

  return {
    isUpdatingStock,
    handleStockChange,
  }
}
